<template>
  <HrPageLayout
    title="Audit Logs"
    subtitle="Immutable trail of sign-ins, changes, and administrative actions"
    icon="📋"
    :breadcrumb-items="[
      { label: 'System Administration' },
      { label: 'Audit Logs' }
    ]"
  >
    <template #header-actions>
      <button type="button" class="hr-btn hr-btn-secondary" @click="clearFilters" :disabled="loading">
        <i class="bx bx-filter-alt"></i> Clear filters
      </button>
      <button type="button" class="hr-btn hr-btn-primary" @click="loadLogs" :disabled="loading">
        <i class="bx bx-refresh" :class="{ spin: loading }"></i>
        {{ loading ? 'Loading…' : 'Refresh' }}
      </button>
    </template>

    <div class="audit-log-shell">
      <div class="audit-log-metrics hr-mb-4">
        <div class="audit-log-metric">
          <span class="audit-log-metric-label">Entries (filtered)</span>
          <strong class="audit-log-metric-value">{{ total }}</strong>
        </div>
        <div class="audit-log-metric">
          <span class="audit-log-metric-label">Page</span>
          <strong class="audit-log-metric-value">{{ page }} / {{ totalPages }}</strong>
        </div>
        <div class="audit-log-metric audit-log-metric-hint">
          <i class="bx bx-info-circle"></i>
          <span>Filter by <strong>user</strong>, <strong>action type</strong>, dates, or search the <strong>description</strong>.</span>
        </div>
      </div>

      <div class="hr-chart-card audit-log-filters-card hr-mb-4">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-search-alt"></i> Filter activity</h5>
        </div>
        <div class="hr-chart-body">
          <div class="audit-log-filter-grid">
            <div class="hr-filter-group audit-log-span-2">
              <label class="hr-form-label">Search description</label>
              <div class="hr-search-wrap">
                <input
                  v-model="filters.search"
                  type="text"
                  class="hr-input"
                  placeholder="Keywords in log text…"
                  @keyup.enter="applyFilters"
                  @input="onSearchInput"
                >
                <i class="bx bx-search-alt search-icon"></i>
              </div>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">User</label>
              <input
                v-model="filters.username"
                type="text"
                class="hr-input"
                placeholder="Name or ID"
                @keyup.enter="applyFilters"
              >
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">Action type</label>
              <select v-model="filters.actionType" class="hr-select" @change="applyFilters">
                <option value="">All types</option>
                <option v-for="t in actionTypes" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">From</label>
              <input v-model="filters.dateFrom" type="date" class="hr-input" @change="applyFilters">
            </div>
            <div class="hr-filter-group">
              <label class="hr-form-label">To</label>
              <input v-model="filters.dateTo" type="date" class="hr-input" @change="applyFilters">
            </div>
            <div class="hr-filter-group audit-log-actions">
              <label class="hr-form-label">&nbsp;</label>
              <button type="button" class="hr-btn hr-btn-primary" @click="applyFilters">
                <i class="bx bx-check"></i> Apply
              </button>
            </div>
          </div>
        </div>
      </div>

      <div class="hr-chart-card audit-log-table-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title"><i class="bx bx-book-content"></i> Event log</h5>
        </div>
        <div class="hr-chart-body hr-p-0">
          <div v-if="loading" class="audit-log-loading">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted hr-mt-2 hr-mb-0">Loading audit logs…</p>
          </div>
          <div v-else-if="logs.length === 0" class="audit-log-empty">
            <i class="bx bx-list-ul"></i>
            <p class="hr-mb-0">No audit logs match your filters.</p>
          </div>
          <div v-else class="hr-table-wrapper audit-log-table-wrap">
            <table class="hr-table hr-table-bordered audit-log-table">
              <thead>
                <tr>
                  <th>When</th>
                  <th>User</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="log in logs" :key="rowKey(log)">
                  <td class="audit-log-when">{{ formatDate(log.actionDate || log.date) }}</td>
                  <td>
                    <div class="audit-log-user">{{ log.username || '—' }}</div>
                    <div v-if="log.idNo" class="audit-log-sub">{{ log.idNo }}</div>
                  </td>
                  <td>
                    <span class="hr-badge audit-action-badge" :class="actionBadgeClass(log.actionType)">
                      {{ log.actionType || '—' }}
                    </span>
                  </td>
                  <td class="audit-log-target">
                    <span>{{ log.tableName || '—' }}</span>
                    <span v-if="log.recordID != null && log.recordID !== ''" class="audit-log-record">#{{ log.recordID }}</span>
                  </td>
                  <td class="audit-log-desc" :title="fullDescription(log)">
                    {{ previewDescription(log) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>

          <div v-if="total > limit && !loading" class="audit-log-pagination">
            <div class="audit-log-page-info">
              Showing {{ (page - 1) * limit + 1 }}–{{ Math.min(page * limit, total) }} of {{ total }}
            </div>
            <nav class="hr-pagination-nav">
              <button
                type="button"
                class="hr-page-btn"
                :disabled="page <= 1"
                @click="goPage(page - 1)"
              >
                <i class="bx bx-chevron-left"></i>
              </button>
              <button
                type="button"
                class="hr-page-btn"
                :disabled="page >= totalPages"
                @click="goPage(page + 1)"
              >
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>
  </HrPageLayout>
</template>

<script>
import { authUrl, execute } from "@/api";
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import "@/assets/css/hr-page-shared.css";

export default {
  name: "AuditLogs",
  components: { HrPageLayout },
  data() {
    return {
      loading: false,
      logs: [],
      total: 0,
      page: 1,
      limit: 50,
      actionTypes: [],
      searchDebounce: null,
      filters: {
        search: "",
        username: "",
        actionType: "",
        dateFrom: "",
        dateTo: ""
      }
    };
  },
  computed: {
    totalPages() {
      return Math.max(1, Math.ceil(this.total / this.limit));
    }
  },
  mounted() {
    this.loadLogs();
  },
  beforeUnmount() {
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
  },
  methods: {
    loadLogs() {
      this.loading = true;
      const data = new FormData();
      data.append("function", "getAuditLogs");
      data.append("page", String(this.page));
      data.append("limit", String(this.limit));
      data.append("username", this.filters.username || "");
      data.append("actionType", this.filters.actionType || "");
      data.append("dateFrom", this.filters.dateFrom || "");
      data.append("dateTo", this.filters.dateTo || "");
      data.append("search", this.filters.search || "");

      execute(data, authUrl)
        .then((res) => {
          this.loading = false;
          if (res.data && res.data.success) {
            const d = res.data.data || {};
            this.logs = d.logs || [];
            this.total = d.total ?? 0;
            if (Array.isArray(d.actionTypes) && d.actionTypes.length) {
              this.actionTypes = d.actionTypes;
            }
          } else {
            this.logs = [];
            this.total = 0;
          }
        })
        .catch(() => {
          this.loading = false;
          this.logs = [];
          this.total = 0;
        });
    },
    applyFilters() {
      this.page = 1;
      this.loadLogs();
    },
    clearFilters() {
      this.filters = {
        search: "",
        username: "",
        actionType: "",
        dateFrom: "",
        dateTo: ""
      };
      this.page = 1;
      this.loadLogs();
    },
    onSearchInput() {
      clearTimeout(this.searchDebounce);
      this.searchDebounce = setTimeout(() => this.applyFilters(), 450);
    },
    goPage(p) {
      if (p < 1 || p > this.totalPages) return;
      this.page = p;
      this.loadLogs();
    },
    rowKey(log) {
      return log.logID || log.id || `${log.actionDate}-${log.username}-${log.description}`;
    },
    fullDescription(log) {
      const t = log.description || log.des || "";
      return t || "";
    },
    previewDescription(log) {
      const t = this.fullDescription(log);
      if (!t) return "—";
      const max = 220;
      return t.length > max ? t.slice(0, max) + "…" : t;
    },
    formatDate(d) {
      if (!d) return "—";
      const dt = new Date(d);
      if (Number.isNaN(dt.getTime())) return "—";
      return dt.toLocaleString("en-GB", { dateStyle: "short", timeStyle: "medium" });
    },
    actionBadgeClass(actionType) {
      if (!actionType) return "audit-badge-muted";
      const v = String(actionType).toLowerCase();
      if (v.includes("login") || v.includes("success")) return "audit-badge-success";
      if (v.includes("fail") || v.includes("denied") || v.includes("error")) return "audit-badge-danger";
      if (v.includes("delete") || v.includes("remove")) return "audit-badge-warning";
      if (v.includes("create") || v.includes("insert") || v.includes("add")) return "audit-badge-info";
      return "audit-badge-neutral";
    }
  }
};
</script>

<style scoped>
.spin {
  animation: spin 0.8s linear infinite;
}
@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

.audit-log-shell {
  max-width: 1320px;
  margin: 0 auto;
  padding: 8px 12px 32px;
  box-sizing: border-box;
}

@media (min-width: 768px) {
  .audit-log-shell {
    padding: 12px 24px 40px;
  }
}

.audit-log-metrics {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 16px 20px;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  border-radius: 16px;
  border: 1px solid #e2e8f0;
}

.audit-log-metric {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 120px;
}

.audit-log-metric-label {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: #64748b;
}

.audit-log-metric-value {
  font-size: 1.35rem;
  font-weight: 700;
  color: #0f172a;
  line-height: 1.2;
}

.audit-log-metric-hint {
  flex: 1;
  min-width: 200px;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  font-size: 13px;
  color: #64748b;
}

.audit-log-metric-hint i {
  font-size: 20px;
  color: #94a3b8;
}

.audit-log-filters-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.audit-log-filter-grid {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

@media (min-width: 992px) {
  .audit-log-filter-grid {
    grid-template-columns: repeat(6, 1fr);
  }
  .audit-log-span-2 {
    grid-column: span 2;
  }
  .audit-log-actions {
    grid-column: span 2;
    justify-self: end;
    max-width: 200px;
  }
}

.hr-search-wrap {
  position: relative;
}

.hr-search-wrap .search-icon {
  position: absolute;
  right: 12px;
  top: 50%;
  transform: translateY(-50%);
  color: #94a3b8;
  pointer-events: none;
}

.hr-search-wrap .hr-input {
  padding-right: 36px;
}

.audit-log-table-card {
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.06);
}

.audit-log-table-wrap {
  margin: 0;
}

.audit-log-table {
  font-size: 14px;
}

.audit-log-table thead th {
  background: #f8fafc;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #475569;
  border-bottom: 2px solid #e2e8f0;
  padding: 14px 16px;
}

.audit-log-table tbody td {
  padding: 14px 16px;
  vertical-align: top;
  border-color: #f1f5f9;
}

.audit-log-when {
  font-variant-numeric: tabular-nums;
  color: #475569;
  font-size: 13px;
  white-space: nowrap;
}

.audit-log-user {
  font-weight: 600;
  color: #0f172a;
}

.audit-log-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.audit-log-target {
  font-size: 13px;
  color: #334155;
}

.audit-log-record {
  display: inline-block;
  margin-left: 6px;
  font-size: 12px;
  color: #94a3b8;
}

.audit-log-desc {
  word-break: break-word;
  font-size: 13px;
  color: #475569;
  line-height: 1.45;
  max-width: min(520px, 42vw);
}

@media (max-width: 991px) {
  .audit-log-desc {
    max-width: none;
  }
}

.audit-action-badge.hr-badge {
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
}

.audit-badge-neutral {
  background: #eef2ff;
  color: #3730a3;
}

.audit-badge-muted {
  background: #f1f5f9;
  color: #64748b;
}

.audit-badge-success {
  background: #dcfce7;
  color: #166534;
}

.audit-badge-danger {
  background: #fee2e2;
  color: #991b1b;
}

.audit-badge-warning {
  background: #fef3c7;
  color: #92400e;
}

.audit-badge-info {
  background: #e0f2fe;
  color: #0369a1;
}

.audit-log-loading,
.audit-log-empty {
  text-align: center;
  padding: 48px 24px;
  color: #64748b;
}

.audit-log-empty i {
  font-size: 48px;
  opacity: 0.35;
  display: block;
  margin-bottom: 12px;
}

.audit-log-pagination {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  border-top: 1px solid #f1f5f9;
  background: #fafbfc;
}

.audit-log-page-info {
  font-size: 13px;
  color: #64748b;
}

.hr-pagination-nav {
  display: flex;
  gap: 8px;
}

.hr-page-btn {
  min-width: 40px;
  height: 40px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #fff;
  color: #475569;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s, border-color 0.15s;
}

.hr-page-btn:hover:not(:disabled) {
  background: #f8fafc;
  border-color: #cbd5e1;
}

.hr-page-btn:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}
</style>
